/**
 * Um audiolivro narrado por gente, do LibriVox.
 *
 * O "Ouvir" que já existia é a voz do navegador lendo o texto em voz alta.
 * Isto aqui é outra coisa: gravação de verdade, feita por voluntários do
 * LibriVox, hospedada no Internet Archive. Domínio público, sem login e sem
 * chave nenhuma.
 *
 * É o `Audiobook` de `lib/features/books/data/models/audiobook.dart`. Onde lá
 * havia `Duration`, aqui há milissegundos: o navegador conta o tempo de áudio
 * em segundos com fração, e milissegundo é o único inteiro que não perde nada
 * na conversão.
 */

/** Uma faixa. Nos audiolivros do LibriVox quase sempre é um capítulo. */
export interface Faixa {
  /** A ordem dentro do livro, começando em 1. Vem do `track` do arquivo. */
  numero: number;
  titulo: string;
  url: string;
  duracaoMs: number;
}

export interface Audiolivro {
  /**
   * O id do item no Internet Archive. É a identidade do audiolivro em todo
   * lugar: na busca, no tocador e na chave que guarda onde a pessoa parou.
   */
  iaId: string;
  titulo: string;
  autor: string;
  capa: string;
  idioma: string;
  ano: string;
  descricao: string;
  /** Quem narrou, quando a descrição do LibriVox diz. Vazio quando não diz. */
  narradores: string;
  duracaoMs: number;
  ouvidos: number;
  /**
   * Vazio até alguém abrir o livro: a busca não traz as faixas, elas vêm de
   * uma segunda chamada.
   */
  faixas: Faixa[];
}

/**
 * "8 h 29 min", que é como alguém diria o tamanho de um audiolivro. Vazio
 * quando o acervo não informou a duração.
 */
export function duracaoEmPalavras(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return '';
  const totalMin = Math.floor(ms / 60000);
  const horas = Math.floor(totalMin / 60);
  const minutos = totalMin % 60;
  if (horas === 0) return `${minutos} min`;
  return minutos === 0 ? `${horas} h` : `${horas} h ${minutos} min`;
}

/** "1:12:34" ou "07:31", que é o que cabe embaixo de uma barra de progresso. */
export function relogio(ms: number): string {
  const seguro = !Number.isFinite(ms) || ms < 0 ? 0 : ms;
  const totalSeg = Math.floor(seguro / 1000);
  const horas = Math.floor(totalSeg / 3600);
  const m = String(Math.floor(totalSeg / 60) % 60).padStart(2, '0');
  const s = String(totalSeg % 60).padStart(2, '0');
  return horas > 0 ? `${horas}:${m}:${s}` : `${m}:${s}`;
}

/** A linha de baixo do resultado: "Português · 2021 · 8 h 29 min". */
export function metaDoAudiolivro(a: Audiolivro): string {
  const partes: string[] = [];
  if (a.idioma) partes.push(a.idioma);
  if (a.ano) partes.push(a.ano);
  const d = duracaoEmPalavras(a.duracaoMs);
  if (d) partes.push(d);
  return partes.join(' · ');
}

/** `Audiobook.comFaixas`: a segunda chamada completa o que a busca trouxe. */
export function comFaixas(
  a: Audiolivro,
  faixas: Faixa[],
  extras: Partial<Pick<Audiolivro, 'duracaoMs' | 'descricao' | 'narradores'>> = {},
): Audiolivro {
  return {
    ...a,
    descricao: extras.descricao ?? a.descricao,
    narradores: extras.narradores ?? a.narradores,
    duracaoMs: extras.duracaoMs ?? a.duracaoMs,
    faixas,
  };
}

/** Um audiolivro vazio, para os mapeadores preencherem o que têm. */
export function audiolivroNovo(campos: Partial<Audiolivro> = {}): Audiolivro {
  return {
    iaId: '',
    titulo: '',
    autor: '',
    capa: '',
    idioma: '',
    ano: '',
    descricao: '',
    narradores: '',
    duracaoMs: 0,
    ouvidos: 0,
    faixas: [],
    ...campos,
  };
}
